/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

interface Props {
  pageData?: {
    title?: string;
    description?: string;
    image?: string;
    robots?: {
      index?: boolean;
      follow?: boolean;
    };
  };
}

const PageHead = ({ pageData = {} }: Props) => {
  const { t } = useTranslation('dbd', { keyPrefix: 'dbd' });
  const { asPath } = useRouter();

  const {
    title = t('default-page-meta.title'),
    description = t('default-page-meta.description'),
    image = t('default-page-meta.img'),
    robots = { index: true, follow: true },
  } = pageData;

  // Page Meta Title (Ex.: Statistics | Dead by Daylight)
  const titlePage = title ? ` | ${title}` : '';
  const titleFull = `Dead by Daylight${titlePage}`;

  // Structured Data for SE index
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Dead by Daylight',
    url: process.env.NEXT_PUBLIC_HOST_URL,
  };
  const structuredDataJson = JSON.stringify(structuredData, null, 2);

  return (
    <Head>
      <title>{titleFull}</title>
      <meta name='description' content={description} />
      <meta name='image' content={image} />
      <meta property='og:title' content={titleFull} />
      <meta property='og:description' content={description} />
      <meta property='og:image' content={image} />
      <meta property='og:image:alt' content={titleFull} />
      <meta property='og:url' content={`${process.env.NEXT_PUBLIC_HOST_URL}${asPath}`} />
      <meta property='og:type' content='website' />
      <meta property='og:site_name' content='Dead by Daylight' />
      <meta name='twitter:title' content={titleFull} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={image} />
      <meta name='twitter:image:alt' content={titleFull} />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:creator' content='DeadbyDaylight' />
      <meta name='robots' content={`${robots.index ? 'index' : 'noindex'}, ${robots.follow ? 'follow' : 'nofollow'}`} />
      {asPath === '/' && <script type='application/ld+json'>{structuredDataJson}</script>}
    </Head>
  );
};

export default PageHead;
